import React, {useMemo} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {isMarkOpenable, openCitationRefSource} from '../../../../../components/prosemirror-editor/components/CitationRefCard';
import {buildCitationMarkAttrsList} from './ForecastCitations';

const MAX_SOURCE_LINE_TEXT_LENGTH = 128;

/** Truncate the "Title — snippet" portion only — the leading "[n] " index prefix is added after, so it's never cut. */
function truncateSourceLineText(text, max = MAX_SOURCE_LINE_TEXT_LENGTH) {
    return text.length > max ? `${text.slice(0, max - 3)}...` : text;
}

/**
 * A source's `title` is frequently auto-generated from the first sentence of the same segment
 * text that becomes `snippet` (see `DocumentService.java#generateTitleFromContent`), which used
 * to produce duplicated-looking lines like "First sentence. — First sentence. Second sentence...".
 * Leads with the snippet (the fuller text) and only appends the title when it's genuinely
 * distinct — i.e. not a case/whitespace-insensitive prefix/equal match of the snippet.
 */
export function buildSourceLineTitleAndSnippet(title, snippet) {
    const normalize = (text) => text.trim().toLowerCase();

    if (snippet) {
        if (!title) {
            return snippet;
        }
        const normalizedTitle = normalize(title);
        const normalizedSnippet = normalize(snippet);
        const isDuplicative = normalizedSnippet === normalizedTitle
            || normalizedSnippet.startsWith(normalizedTitle)
            || normalizedTitle.startsWith(normalizedSnippet);
        return isDuplicative ? snippet : `${snippet} — ${title}`;
    }

    return title || 'Untitled source';
}

/**
 * MINSKY-16818 (ticket 02): a flat, footnote-style sources list ("[n] Title — snippet") for a
 * single prediction/key-driver, rendered directly beneath that item's own preview text — never
 * one combined list for the whole forecast. Line numbers reuse the exact same forecast-wide
 * numbering (`sourcesByKey`/`numberByKey`) as that item's inline "[n]" markers
 * (`ForecastPreviewText`), so the two always agree. Each line opens the cited source through the
 * same shared `openCitationRefSource` dispatch the in-document citation popover uses — gated by
 * the same `isMarkOpenable` check, so a source with no resolvable identifier renders as plain
 * (non-interactive) text instead of a dead link.
 */
export function ForecastSourcesList({citedSources, sourcesByKey, numberByKey}) {
    const attrsList = useMemo(
        () => buildCitationMarkAttrsList(citedSources, sourcesByKey, numberByKey)
            .slice()
            .sort((a, b) => a.number - b.number),
        [citedSources, sourcesByKey, numberByKey],
    );

    if (!attrsList.length) return null;

    return (
        <Stack component="ol" className="forecast-sources-list" spacing={0.25} sx={{listStyle: 'none', m: 0, p: 0, width: '100%'}}>
            {attrsList.map((attrs) => {
                const openable = isMarkOpenable(attrs);
                const titleAndSnippet = buildSourceLineTitleAndSnippet(attrs.title, attrs.snippet);
                const lineText = `[${attrs.number}] ${truncateSourceLineText(titleAndSnippet)}`;
                return (
                    <Typography
                        key={attrs.key}
                        component="li"
                        variant="caption"
                        color="text.secondary"
                        sx={{width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                    >
                        {openable ? (
                            <Box
                                component="span"
                                role="button"
                                tabIndex={0}
                                onClick={() => openCitationRefSource(attrs)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openCitationRefSource(attrs);
                                    }
                                }}
                                sx={{cursor: 'pointer', '&:hover': {textDecoration: 'underline'}}}
                            >
                                {lineText}
                            </Box>
                        ) : lineText}
                    </Typography>
                );
            })}
        </Stack>
    );
}
